/* eslint-disable max-lines */
import { useFavorites } from '@hooks/useFavorites'
import { useIsNativeApp } from '@hooks/useIsNativeApp'
import useFontSizes from '@hooks/useFontSizes'
import classNames from 'classnames'
import { useAuth } from 'contexts/AuthContext'
import dynamic from 'next/dynamic'
import Link from 'next/link'
import { useRouter } from 'next/router'
import { useCallback, useEffect, useRef, useState } from 'react'
import {
  FaCog,
  FaInfoCircle,
  FaMosque,
  FaPatreon,
  FaPray,
  FaSignOutAlt,
  FaStar,
  FaTimes,
  FaUserCircle,
} from 'react-icons/fa'
import { MdMenu } from 'react-icons/md'
import { contentLibrary } from '../../consts'
import SearchIconAndModal from '../SearchIconAndModal'
import { CollapsibleSection } from './CollapsibleSection'
import { navButtons, otherButtons, otherContentItems, tesbihatItems } from './navConfig'
import { NavigationLink } from './NavigationLink'

const ThemeButtons = dynamic(() => import('../ThemeButtons'), { ssr: false })
const BackButtonDynamicImported = dynamic(() => import('../BackButton'), {
  ssr: false,
})

const ReminderNotification = dynamic(() => import('../ReminderNotification'), {
  ssr: false,
})

export const Nav = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const [isVisible, setIsVisible] = useState(true)
  const navbarRef = useRef<HTMLElement>(null)
  const [navbarHeight, setNavbarHeight] = useState(52)
  const isNativeAvailable = useIsNativeApp()
  const filteredOtherButtons = isNativeAvailable
    ? otherButtons.filter((button) => button.href !== '/uygulama')
    : otherButtons

  const lastScrollY = useRef(0)
  const router = useRouter()
  const { user, logout } = useAuth()
  const { kuranFontSize, kuranMealFontSize } = useFontSizes()

  // Track favorite content library items
  const { favorites, isFavorite, toggleFavorite } = useFavorites()

  useEffect(() => {
    const navbar = navbarRef.current
    if (!navbar) return

    /** Measures the actual navbar height for sticky reader information. */
    const measureNavbar = () => setNavbarHeight(navbar.offsetHeight)
    measureNavbar()
    const observer = new ResizeObserver(measureNavbar)
    observer.observe(navbar)
    return () => observer.disconnect()
  }, [])

  useEffect(() => {
    // Share the navbar's existing visibility decision instead of duplicating scroll logic.
    document.documentElement.style.setProperty(
      '--reader-navbar-height',
      `${isVisible || isMenuOpen ? navbarHeight : 0}px`,
    )
    return () => {
      document.documentElement.style.removeProperty('--reader-navbar-height')
    }
  }, [isVisible, isMenuOpen, navbarHeight])

  useEffect(() => {
    setIsMenuOpen(false)
  }, [router.pathname])

  useEffect(() => {
    if (isMenuOpen) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = 'unset'
    }
  }, [isMenuOpen])

  const openNativeSettings = useCallback(() => {
    if (typeof window === 'undefined') return
    const webview = (
      window as unknown as {
        ReactNativeWebView?: { postMessage?: (data: string) => void }
      }
    ).ReactNativeWebView
    webview?.postMessage?.(JSON.stringify({ type: 'openSettings' }))
    setIsMenuOpen(false)
  }, [])

  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY
      const threshold = 80 // Threshold to keep navbar visible at the top

      if (currentScrollY <= threshold) {
        setIsVisible(true)
      } else if (currentScrollY < lastScrollY.current) {
        // Scrolling up
        setIsVisible(true)
      } else if (currentScrollY > lastScrollY.current) {
        // Scrolling down
        setIsVisible(false)
      }

      lastScrollY.current = currentScrollY
    }

    handleScroll()

    window.addEventListener('scroll', handleScroll, { passive: true })
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  return (
    <>
      <style>
        {`
      .ayah-arabic {
        font-size: ${kuranFontSize}px;
        direction: rtl;
      }

      .ayah-meal {
        font-size: ${kuranMealFontSize}px;
        direction: ltr;
        font-family: sans-serif;
      }
    `}
      </style>
      <ReminderNotification />
      {/* Dynamic spacer that adjusts when banner is present */}
      <div
        className="mb-2 transition-all duration-300 ease-in-out"
        style={{ height: 'calc(52px + var(--banner-height, 0px))' }}
      />
      <div className="relative">
        {/* Main Navbar */}
        <nav
          ref={navbarRef}
          style={{ top: 'var(--banner-height, 0px)' }}
          className={classNames(
            'bg-white/90 backdrop-blur-lg dark:bg-primary-dark/90',
            'flex items-center justify-between px-4 py-2 lg:px-6',
            'fixed left-0 right-0 z-40 transition-all duration-500 ease-in-out',
            'border-b border-primary-200/30 dark:border-primary-dark-400/30',
            'shadow-sm shadow-primary-500/5',
            {
              '-translate-y-full opacity-0': !isVisible && !isMenuOpen,
              'translate-y-0 opacity-100': isVisible || isMenuOpen,
            },
          )}
        >
          {/* Gradient accent line at top */}
          <div className="absolute top-0 left-0 right-0 h-[2px] bg-linear-to-r from-primary-400 via-primary-500 to-secondary-400" />

          <div className="flex items-center">
            <BackButtonDynamicImported />
            <Link
              href="/"
              className="group inline-flex items-center space-x-2.5 px-2.5 py-1.5 rounded-xl transition-all duration-200 hover:bg-primary-50 dark:hover:bg-primary-dark-400/50 hover:scale-[1.02] active:scale-[0.98]"
            >
              <img
                src={'/icons/icon-192.png'}
                width={28}
                height={28}
                className="rounded-lg shadow-sm transition-transform group-hover:rotate-[-5deg]"
                alt="Logo"
              />
              <span className="font-bold text-lg bg-linear-to-r from-primary-600 to-primary-500 dark:from-primary-300 dark:to-primary-200 bg-clip-text text-transparent">
                HAYIRHAH
              </span>
            </Link>
          </div>

          <div className="flex items-center gap-1">
            <SearchIconAndModal />
            <ThemeButtons className="block" />
            <button
              aria-label="Open Menu"
              title="Open Menu"
              className="p-2.5 transition-all duration-200 rounded-xl hover:bg-primary-50 dark:hover:bg-primary-dark-400/50 hover:scale-105 active:scale-95"
              onClick={() => setIsMenuOpen(!isMenuOpen)}
            >
              {user ? (
                <div className="w-6 h-6 rounded-full bg-linear-to-br from-primary-400 to-secondary-400 flex items-center justify-center text-white text-xs font-bold">
                  {(user.displayName?.[0] || user.email?.[0] || 'U').toUpperCase()}
                </div>
              ) : (
                <MdMenu size={24} className="text-primary-dark dark:text-primary-100" />
              )}
            </button>
          </div>
        </nav>

        {/* Overlay */}
        {isMenuOpen && (
          <div
            className="fixed inset-0 z-40 bg-black/40 backdrop-blur-sm nav-overlay-enter"
            onClick={() => setIsMenuOpen(false)}
          />
        )}

        {/* Sidebar */}
        {isMenuOpen && (
          <div className="fixed right-0 top-0 z-50 flex h-screen w-[340px] sm:w-[360px] flex-col overflow-hidden bg-white dark:bg-primary-dark shadow-2xl shadow-black/20 nav-sidebar-enter">
            {/* Sidebar Header with Gradient */}
            <div className="relative">
              {/* Gradient Background */}
              <div className="absolute inset-0 bg-linear-to-br from-primary-500 via-primary-600 to-primary-700 dark:from-primary-dark-300 dark:via-primary-dark-400 dark:to-primary-dark-500" />
              <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxwYXRoIGQ9Ik0zNiAxOGMzLjMxNCAwIDYgMi42ODYgNiA2cy0yLjY4NiA2LTYgNi02LTIuNjg2LTYtNiAyLjY4Ni02IDYtNiIgc3Ryb2tlPSJyZ2JhKDI1NSwyNTUsMjU1LDAuMSkiIHN0cm9rZS13aWR0aD0iMiIvPjwvZz48L3N2Zz4=')] opacity-30" />

              <div className="relative px-5 py-5">
                <div className="flex items-center justify-between">
                  <span className="text-lg font-semibold text-white">Menü</span>
                  <button
                    onClick={() => setIsMenuOpen(false)}
                    title="Menüyü Kapat"
                    aria-label="Menüyü Kapat"
                    className="p-2 transition-all duration-200 rounded-xl bg-white/10 hover:bg-white/20 text-white hover:scale-105 active:scale-95"
                  >
                    <FaTimes size={14} />
                  </button>
                </div>

                {/* User Card */}
                {user ? (
                  <div className="mt-4 p-3 rounded-xl bg-white/10 backdrop-blur-sm nav-item-animate nav-delay-1">
                    <div className="flex items-center gap-3">
                      <div className="w-11 h-11 rounded-full bg-linear-to-br from-secondary-300 to-secondary-500 flex items-center justify-center text-white text-lg font-bold shadow-lg">
                        {(user.displayName?.[0] || user.email?.[0] || 'U').toUpperCase()}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-white font-medium truncate">
                          {user.displayName || user.email.split('@')[0]}
                        </p>
                        <p className="text-white/70 text-xs truncate">{user.email}</p>
                      </div>
                    </div>
                  </div>
                ) : (
                  <Link
                    href="/giris-yap"
                    onClick={() => setIsMenuOpen(false)}
                    className="mt-4 flex items-center gap-3 p-3 rounded-xl bg-white/10 backdrop-blur-sm hover:bg-white/20 transition-all duration-200 nav-item-animate nav-delay-1"
                  >
                    <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
                      <FaUserCircle className="text-white text-xl" />
                    </div>
                    <span className="text-white font-medium">Giriş Yap / Kayıt Ol</span>
                  </Link>
                )}

                {/* Patreon Support Button */}
                <a
                  href="https://patreon.com/Hayirhah?utm_medium=unknown&utm_source=join_link&utm_campaign=creatorshare_creator&utm_content=copyLink"
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => setIsMenuOpen(false)}
                  className="mt-3 flex items-center gap-3 p-3 rounded-xl bg-red-500/20 backdrop-blur-sm hover:bg-red-500/30 transition-all duration-200 nav-item-animate nav-delay-2"
                >
                  <div className="w-10 h-10 rounded-full bg-red-500 flex items-center justify-center">
                    <FaPatreon className="text-white text-lg" />
                  </div>
                  <div className="flex flex-col">
                    <span className="text-white font-medium text-sm">Bizi Destekle</span>
                    <span className="text-white/70 text-xs">Patreon</span>
                  </div>
                </a>
              </div>
            </div>

            {/* Scrollable Content */}
            <div className="flex-1 px-4 py-5 overflow-y-auto">
              {/* Main Navigation */}
              <div className="space-y-1">
                {navButtons.map(({ href, name, icon: Icon }, index) => (
                  <NavigationLink
                    closeMenu={() => setIsMenuOpen(false)}
                    key={href}
                    href={href}
                    name={name}
                    icon={Icon}
                    animationDelay={index + 2}
                  />
                ))}
              </div>

              {/* Favorites */}
              <CollapsibleSection
                title="Favoriler"
                storageKey="nav-favorites-expanded"
                defaultExpanded={true}
                icon={<FaStar className="text-yellow-500 dark:text-yellow-400" />}
                accentColor="yellow"
              >
                {favorites.length > 0 ? (
                  favorites.map((url) => {
                    const contentItem = contentLibrary.find((item) => item.url === url)
                    return contentItem ? (
                      <NavigationLink
                        closeMenu={() => setIsMenuOpen(false)}
                        key={`favorite-${url}`}
                        href={'/' + url}
                        name={contentItem.name}
                        newFeature={contentItem.isNew}
                        showFavoriteButton={true}
                        isFavorite={true}
                        onToggleFavorite={() => toggleFavorite(url)}
                      />
                    ) : null
                  })
                ) : (
                  <p className="px-3 py-2 text-sm text-slate-500 dark:text-slate-400 italic">
                    Henüz favori eklemediniz. Aşağıdaki duaların yanındaki yıldıza
                    tıklayarak favorilerinize ekleyebilirsiniz.
                  </p>
                )}
              </CollapsibleSection>

              {/* Namaz Tesbihatları */}
              <CollapsibleSection
                title="Namaz Tesbihatları"
                storageKey="nav-tesbihat-expanded"
                defaultExpanded={false}
                icon={<FaMosque className="text-primary-500 dark:text-primary-400" />}
                accentColor="primary"
              >
                {tesbihatItems.map(({ url, name, isNew }) => (
                  <NavigationLink
                    closeMenu={() => setIsMenuOpen(false)}
                    key={url}
                    href={'/' + url}
                    name={name}
                    newFeature={isNew}
                    showFavoriteButton={true}
                    isFavorite={isFavorite(url)}
                    onToggleFavorite={() => toggleFavorite(url)}
                  />
                ))}
              </CollapsibleSection>

              {/* Diğer İçerikler */}
              <CollapsibleSection
                title="Diğer İçerikler"
                storageKey="nav-other-content-expanded"
                defaultExpanded={true}
                icon={<FaPray className="text-primary-500 dark:text-primary-400" />}
                accentColor="primary"
              >
                {otherContentItems.map(({ url, name, isNew }) => (
                  <NavigationLink
                    closeMenu={() => setIsMenuOpen(false)}
                    key={url}
                    href={'/' + url}
                    name={name}
                    newFeature={isNew}
                    showFavoriteButton={true}
                    isFavorite={isFavorite(url)}
                    onToggleFavorite={() => toggleFavorite(url)}
                  />
                ))}
              </CollapsibleSection>

              {/* Diğerleri */}
              <CollapsibleSection
                title="Diğerleri"
                storageKey="nav-others-expanded"
                defaultExpanded={false}
                icon={<FaInfoCircle className="text-slate-500 dark:text-slate-400" />}
                accentColor="slate"
              >
                {isNativeAvailable && (
                  <NavigationLink
                    closeMenu={() => setIsMenuOpen(false)}
                    href="/__native-settings"
                    name="Uygulama Ayarları"
                    icon={FaCog}
                    onClick={(event) => {
                      event.preventDefault()
                      openNativeSettings()
                    }}
                  />
                )}
                {filteredOtherButtons.map(({ href, name, external, icon: Icon }) => (
                  <NavigationLink
                    closeMenu={() => setIsMenuOpen(false)}
                    external={external}
                    key={href}
                    href={href}
                    name={name}
                    icon={Icon}
                  />
                ))}
              </CollapsibleSection>
            </div>

            {/* Logout Button (Pinned) */}
            {user && (
              <div className="px-4 py-4 border-t border-slate-200 dark:border-primary-dark-400 bg-white/95 dark:bg-primary-dark/95 backdrop-blur-sm">
                <button
                  onClick={logout}
                  className="flex items-center gap-3 w-full px-4 py-3 font-medium transition-all duration-200 rounded-xl bg-red-50 hover:bg-red-100 dark:bg-red-500/10 dark:hover:bg-red-500/20 text-red-600 dark:text-red-400 hover:scale-[1.01] active:scale-[0.99]"
                >
                  <FaSignOutAlt className="text-lg" />
                  <span>Çıkış Yap</span>
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </>
  )
}

export default Nav
